// @flow
import type {Node} from 'react';
import moment from 'moment';
import {Icon, IconNames, GridActionsCellItem} from '@wellstone-solutions/web';
import type {Member} from 'api/v2';
import {useOrganizationEventsList} from 'modules/organization';
import {useStores} from 'hooks/useStores';
import {AccessControl} from 'modules/rbac';
import {
  PATIENT_CREATED_REINVITE_MINUTES,
  LAST_INVITATION_REINVITE_MINUTES,
} from '../../../constants';

type PropsType = {
  member: Member,
  showInMenu: boolean,
  onClick: (member: Member) => void,
};

export const ResendInvitation = ({
  member,
  showInMenu,
  onClick,
}: PropsType): Node => {
  const {RBACStore} = useStores();

  const {isLoading, data} = useOrganizationEventsList({
    memberId: member.id,
    eventCategory: 'staff_sent_invite',
  });

  const now = moment();

  // Check if user lacks permission or member already used the app
  const isDisabled =
    isLoading ||
    !RBACStore.hasAccess(AccessControl.members.editMember) ||
    !!member.data?.lastAppOpen;

  // Check if the member was created too recently to send another invitation
  const isNewlyCreatedMember =
    now.diff(moment(member.created), 'minutes') <
    PATIENT_CREATED_REINVITE_MINUTES;

  // Get the most recent invitation event, if any
  const lastInviteEvent = data?.results.length > 0 ? data.results[0] : null;

  // Check if an invitation was sent too recently
  const wasRecentlyInvited =
    lastInviteEvent &&
    now.diff(moment(lastInviteEvent.created), 'minutes') <
      LAST_INVITATION_REINVITE_MINUTES;

  // Combine the cooldown checks
  const isInInviteCooldown = isNewlyCreatedMember || wasRecentlyInvited;

  return (
    <GridActionsCellItem
      label="Resend Invitation"
      key="resendInvitation"
      icon={<Icon name={IconNames.Mail} size={22} />}
      showInMenu={showInMenu}
      onClick={() => onClick(member)}
      disabled={isDisabled || isInInviteCooldown}
    />
  );
};
